import type { CheckboxProps } from '@ben/orion/checkbox';
import Checkbox from '@ben/orion/checkbox';
import { memo, useCallback, useEffect, useEffectEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface CheckboxValidationError {
  hasError: boolean;
  errorMessage: string;
}

export interface RequiredCheckboxWrapperProps extends CheckboxProps {
  requireTrue?: boolean;
  validationErrorCallback?: (error: CheckboxValidationError) => void;
}

/**
 * Adds validation logic to ensure checkbox is checked when requireTrue is set
 */
const RequiredCheckboxWrapper = ({
  id,
  requireTrue = false,
  value = false,
  onChange,
  validationErrorCallback,
  label,
  hasError: externalHasError,
  errorMessage: externalErrorMessage,
  ...rest
}: RequiredCheckboxWrapperProps) => {
  const { t } = useTranslation();

  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    setHasInteracted(false);
  }, [id]);

  const handleChange = useCallback(
    (isChecked: boolean) => {
      if (onChange) onChange(isChecked);
      setHasInteracted(true);

      if (requireTrue) {
        const hasError = !isChecked;
        validationErrorCallback?.({
          hasError,
          errorMessage: hasError ? t('phrases.checkboxRequired') : '',
        });
      }
    },
    [onChange, requireTrue, validationErrorCallback, t]
  );

  // The host hands down a new callback identity every render, so it is read
  // as an event rather than as an effect dependency.
  const handleSetValidationError = useEffectEvent(
    (error: CheckboxValidationError) => {
      if (validationErrorCallback) validationErrorCallback(error);
    }
  );

  useEffect(
    function reportValidationToParent() {
      if (requireTrue) {
        const hasError = !value;
        handleSetValidationError({
          hasError,
          errorMessage:
            hasInteracted && hasError ? t('phrases.checkboxRequired') : '',
        });
      }
    },
    [requireTrue, value, hasInteracted, t]
  );

  const showValidationError = hasInteracted && requireTrue && !value;
  const hasError = externalHasError || showValidationError;
  const errorMessage = showValidationError
    ? t('phrases.checkboxRequired')
    : externalErrorMessage;
  return (
    <Checkbox
      id={id}
      label={label}
      value={value}
      onChange={handleChange}
      hasError={
        requireTrue ? (hasInteracted ? hasError : false) : externalHasError
      }
      errorMessage={requireTrue ? errorMessage : externalErrorMessage}
      {...rest}
    />
  );
};

export default memo(RequiredCheckboxWrapper);
